import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useGeo, formatPrice, distanceLabel } from "../lib/locale";
import { TID } from "../constants/catalog";
import { MapPin, Calendar, ShieldAlert, Flag, MessageCircle, ChevronLeft, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const geo = useGeo();
  const [listing, setListing] = useState(null);
  const [active, setActive] = useState(0);
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => setListing(null));
  }, [id]);

  const startChat = async () => {
    if (!user) return nav("/login", { state: { redirect: `/listing/${id}` } });
    try {
      const r = await api.post("/chats", { listing_id: id });
      nav(`/chats/${r.data.chat_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start chat");
    }
  };

  const report = async () => {
    if (!user) return nav("/login");
    const reason = window.prompt("Why are you reporting this listing?");
    if (!reason) return;
    setReporting(true);
    try {
      await api.post("/reports", { listing_id: id, reason });
      toast.success("Reported. Our team will review.");
    } finally {
      setReporting(false);
    }
  };

  if (!listing) return <div className="pbp-section py-20 text-center text-[#86868b]">Loading…</div>;

  return (
    <div className="pbp-section py-8 md:py-12">
      <button onClick={() => nav(-1)} className="pbp-btn-ghost mb-6 -ml-3">
        <ChevronLeft className="w-4 h-4" strokeWidth={1.8} /> Back
      </button>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <div className="pbp-card overflow-hidden">
            <div className="aspect-[4/3] bg-[#f5f5f7]">
              {listing.images?.[active] && (
                <img
                  data-testid={TID.detail.image(active)}
                  src={listing.images[active].url}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            {listing.images?.length > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto">
                {listing.images.map((img, i) => (
                  <button
                    key={img.path}
                    onClick={() => setActive(i)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${i === active ? "border-[#0071e3]" : "border-transparent"}`}
                  >
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="pbp-card mt-6 p-6 md:p-8">
            <h2 className="text-xl font-semibold tracking-tight mb-3">Description</h2>
            <p className="text-[#1d1d1f]/85 whitespace-pre-wrap leading-relaxed">{listing.description}</p>
          </div>
        </div>

        <aside className="lg:col-span-5 space-y-6">
          <div className="pbp-card p-6 md:p-8">
            <div className="text-xs uppercase tracking-[0.18em] text-[#86868b] font-semibold">
              {listing.category} · {listing.year}
            </div>
            <h1 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight">
              {listing.part_name === "Other" && listing.part_custom_text ? listing.part_custom_text : listing.part_name}
            </h1>
            <div className="mt-2 text-[#1d1d1f]/80">
              Model: <b>{listing.model_number}</b>
              {listing.model_name && <span className="block text-sm text-[#86868b] mt-0.5">{listing.model_name}</span>}
            </div>
            <div className="mt-1 inline-flex pbp-chip">{listing.condition}</div>

            <div className="mt-6 text-3xl font-semibold tracking-tight">
              {formatPrice(listing.price, listing.currency)}
            </div>

            <div className="mt-5 text-sm text-[#86868b] space-y-1.5">
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4" strokeWidth={1.5} />{listing.city}, {listing.country}{listing.distance_km != null && <span className="text-[#0071e3]">· {distanceLabel(listing.distance_km, geo?.country_code)}</span>}</div>
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4" strokeWidth={1.5} />Listed {new Date(listing.created_at).toLocaleDateString()}</div>
            </div>

            <button
              data-testid={TID.detail.chatCta}
              onClick={startChat}
              disabled={user && user.user_id === listing.user_id}
              className="pbp-btn-primary w-full mt-6"
            >
              <MessageCircle className="w-4 h-4" /> {user && user.user_id === listing.user_id ? "This is your listing" : "Chat with seller"}
            </button>

            {user && user.user_id === listing.user_id && (
              <button
                data-testid="detail-edit-listing"
                onClick={() => nav(`/sell/${listing.listing_id}`)}
                className="pbp-btn-secondary w-full mt-2"
              >
                <Pencil className="w-4 h-4" /> Edit listing
              </button>
            )}

            {listing.status === "pending_review" && user?.user_id === listing.user_id && (
              <div className="mt-3 rounded-xl border border-[#ff9500]/40 bg-[#ff9500]/10 px-3 py-2 text-xs text-[#7a3f00] flex gap-2 items-start">
                <ShieldAlert className="w-3.5 h-3.5 mt-0.5" /> Your recent edits are held for AI/moderator review. Listing is temporarily hidden from search.
              </div>
            )}

            <button onClick={report} disabled={reporting} data-testid={TID.detail.report} className="pbp-btn-ghost w-full mt-2 text-[#ff3b30] hover:bg-[#ff3b30]/8">
              <Flag className="w-4 h-4" strokeWidth={1.6} /> Report listing
            </button>
          </div>

          <Link to={`/users/${listing.user_id}`} data-testid={TID.detail.sellerCard} className="pbp-card flex items-center gap-4 p-5 hover:bg-[#fafafd]">
            {listing.user_picture ? (
              <img src={listing.user_picture} alt="" className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#0071e3] grid place-items-center text-white font-semibold">
                {listing.user_name?.[0]?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{listing.user_name}</div>
              <div className="text-xs text-[#86868b]">Seller</div>
            </div>
          </Link>

          <div className="rounded-2xl border border-[#ff9500]/30 bg-[#ff9500]/8 p-5 text-[#9a4e00] text-sm flex gap-3">
            <ShieldAlert className="w-5 h-5 mt-0.5" strokeWidth={1.8} />
            <p>
              Verify the seller&rsquo;s identity, inspect the product before payment, and always meet
              in a safe public place. PartByPart does not handle payments and is not liable for transactions.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
